/* ===========================================================================
   WWC — één live region voor de keuze op de productpagina
   ===========================================================================

   HET PROBLEEM
   Op de productpagina veranderen prijs, maat, materiaal en beschikbaarheid
   zonder herladen zodra de bezoeker een variant kiest. Voor wie kijkt is dat
   duidelijk. Voor wie met een schermlezer werkt gebeurt er niets: de knop
   krijgt de focus, en de prijs die er drie regels boven staat verandert
   geruisloos. Je hoort dus niet wat je zojuist hebt gedaan.

   WAAROM ÉÉN REGION EN NIET DRIE
   Een region per waarde geeft drie meldingen na één klik, en die vallen in
   willekeurige volgorde over elkaar heen. Eén zin met alles erin is korter dan
   drie halve.

   WAT ER WEL EN NIET IN ZIT
   Wel: maat, materiaal, prijs, en beschikbaarheid als het product uitverkocht
   is. Niet: de levertijd. Die wordt namelijk helemaal niet per variant
   herrekend - wwc-cro-delivery.js draait alleen bij het laden van de pagina en
   hangt aan template.suffix, niet aan de variant. Een vaste zin die bij elke
   klik wordt meegelezen maakt de melding langer zonder iets toe te voegen, en
   dat is precies waarom mensen live regions uitzetten.

   POLITE, NIET ASSERTIVE
   Dit is de bevestiging van een handeling die de bezoeker zelf net heeft
   gedaan, geen waarschuwing. Assertive onderbreekt de schermlezer midden in de
   naam van de knop waar hij op staat.

   DE REGION STAAT BUITEN DE PRODUCTSECTIE
   Motion hertekent bij een variantwissel de hele productsectie (zie de
   toelichting in wwc-selection.js). Een live region die mee hertekend wordt,
   verstomt: de browser ziet een nieuw element in plaats van gewijzigde tekst.
   Vandaar dat dit script hem zelf aan <body> hangt.

   DRIE VALKUILEN, ALLE DRIE AFGEVANGEN
   1. Bij het laden roept wwc:selection ook, met reason 'init'. Zonder de
      startvlag hieronder zou de pagina bij openen meteen de prijs voorlezen.
   2. wwc-selection.js leest per klik DRIE keer opnieuw (rAF, 120 ms, 400 ms)
      omdat Motion de sectie in stappen bijwerkt. Zonder debounce is dat drie
      meldingen per klik.
   3. Klikt iemand snel drie keer op dezelfde optie, dan is de zin identiek;
      dan zeggen we niets. Een schermlezer meldt een ongewijzigde region niet
      opnieuw, maar we schrijven hem ook niet opnieuw weg.
   ========================================================================= */
(function () {
  'use strict';

  var VERTRAGING = 600;

  var STRINGS = {
    nl: { uit: 'Uitverkocht', gekozen: 'Gekozen' },
    en: { uit: 'Sold out', gekozen: 'Selected' },
    de: { uit: 'Ausverkauft', gekozen: 'Gewählt' },
    fr: { uit: 'Épuisé', gekozen: 'Sélectionné' }
  };
  var lang = (document.documentElement.lang || 'nl').slice(0, 2).toLowerCase();
  var T = STRINGS[lang] || STRINGS.en;

  /* Alleen op een productpagina met de keuze-module. Zonder deze poort zou de
     region op elke pagina in de DOM staan en nooit iets zeggen. */
  if (!document.querySelector('[data-product-price], [data-maat-tekst]')) return;

  var region = document.createElement('p');
  region.className = 'visually-hidden';   /* themabrede klasse, theme.css.liquid */
  region.setAttribute('role', 'status');
  region.setAttribute('aria-live', 'polite');
  region.setAttribute('aria-atomic', 'true');
  region.setAttribute('data-wwc-keuzemelding', '');
  document.body.appendChild(region);

  /* De bezoeker moet EERST iets gedaan hebben. 'init' afvangen is niet genoeg:
     sommige apps vuren wwc:selection ook na een async prijsupdate. */
  var gestart = false;
  ['pointerdown', 'keydown'].forEach(function (ev) {
    document.addEventListener(ev, function () { gestart = true; }, { once: true, capture: true });
  });

  function tekst(sel) {
    var el = document.querySelector(sel);
    if (!el) return '';
    /* offsetParent === null betekent verborgen. De streepprijs staat er ook
       leeg in bij producten zonder korting; die mag niet meegelezen worden. */
    if (el.offsetParent === null && el.getClientRects().length === 0) return '';
    return (el.textContent || '').replace(/\s+/g, ' ').trim();
  }

  function bouwZin() {
    var k = (window.WWC && window.WWC.getSelection) ? window.WWC.getSelection() : null;
    var delen = [];

    /* De volledige maat ('80 × 16,5 cm') zegt meer dan de gekozen optie
       ('80 cm'), dus die heeft voorrang. Staat hij er niet, dan de optie. */
    var maat = tekst('[data-maat-tekst]');
    if (maat) delen.push(maat);
    else if (k && k.size) delen.push(k.size);

    if (k && k.material) delen.push(k.material);

    /* De valuta erbij, maar ALLEEN als het prijselement hem zelf niet toont.

       Op deze winkel staat er letterlijk "139,00" - het euroteken is bewust
       weggelaten op de EU-markt. Voor wie kijkt is dat prima: die ziet de rest
       van de pagina. Een schermlezer maakt er "honderdnegenendertig komma nul
       nul" van, zonder eenheid, en dan is de melding waardeloos.

       De code komt uit Shopify.currency.active, dus uit de winkel zelf en niet
       uit een aanname over de markt. Zit er wél een symbool of code in de
       zichtbare tekst, dan laten we hem met rust - anders krijg je "€ 139,00
       EUR". */
    var prijs = tekst('[data-product-price]');
    if (prijs) {
      var heeftEenheid = /[^\d\s.,'’-]/.test(prijs);
      var val = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || '';
      delen.push(heeftEenheid || !val ? prijs : prijs + ' ' + val);
    }

    if (k && k.available === false) delen.push(T.uit);

    if (!delen.length) return '';
    return T.gekozen + ': ' + delen.join(', ') + '.';
  }

  var timer = null;
  var vorige = '';

  function meld() {
    if (!gestart) return;
    clearTimeout(timer);
    timer = setTimeout(function () {
      var zin = bouwZin();
      if (!zin || zin === vorige) return;
      vorige = zin;
      region.textContent = zin;
    }, VERTRAGING);
  }

  document.addEventListener('wwc:selection', function (e) {
    if (e && e.detail && e.detail.reason === 'init') return;
    meld();
  });
})();
